import type { Namespace } from '../types';

// DE drafted (no login screen in the spec HTML) — queued for native review in the
// task file's "DE review queue". Email/password/brand-tag live in `common`.
export const login = {
  // R-239: each OTP box needs its own name for a screen reader — "digit 3 of 6"
  // is the only way the segmented field is navigable without sight.
  otpDigit: { de: 'Ziffer {n} von {total}', en: 'digit {n} of {total}' },
  welcome: { de: 'Willkommen zurück', en: 'Welcome back' },
  lead: { de: 'Melden Sie sich bei Ihrer Farm-Leitkonsole an.', en: 'Sign in to your farm control console.' },
  emailPlaceholder: { de: 'sie@firma.com', en: 'you@company.com' },
  passwordPlaceholder: { de: '••••••••', en: '••••••••' },
  signIn: { de: 'Anmelden', en: 'Sign in' },
  signingIn: { de: 'Anmeldung läuft…', en: 'Signing in…' },
  forgotPassword: { de: 'Passwort vergessen?', en: 'Forgot password?' },
  errorBadCredentials: { de: 'E-Mail oder Passwort falsch.', en: 'Incorrect email or password.' },
  errorGeneric: {
    de: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    en: 'Could not sign in. Please try again.',
  },

  // 2FA (R-076/D-080)
  mfaEnrolTitle: { de: 'Zwei-Faktor einrichten', en: 'Set up two-factor' },
  mfaEnrolLead: {
    de: 'Scannen Sie diesen QR-Code mit einer Authenticator-App (Google Authenticator, Microsoft Authenticator, Authy …) und geben Sie den angezeigten 6-stelligen Code ein.',
    en: 'Scan this QR code with any authenticator app (Google Authenticator, Microsoft Authenticator, Authy …), then enter the 6-digit code it shows.',
  },
  mfaCodeTitle: { de: 'Bestätigen Sie Ihre Identität', en: 'Verify your identity' },
  mfaCodeLead: {
    de: 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.',
    en: 'Enter the 6-digit code from your authenticator app.',
  },
  mfaCodeLabel: { de: 'Authentifizierungscode', en: 'Authentication code' },
  mfaVerify: { de: 'Bestätigen', en: 'Verify' },
  mfaVerifying: { de: 'Wird geprüft…', en: 'Verifying…' },
  mfaManualKey: { de: 'Schlüssel manuell eingeben', en: 'Enter key manually' },
  mfaBadCode: { de: 'Falscher Code. Bitte erneut versuchen.', en: 'Incorrect code. Please try again.' },
  // A spent challenge (5 attempts used, or expired): no code can verify it, so
  // the message must send the operator back to the start instead of inviting a
  // retry — which is what the old generic "please try again" did.
  mfaChallengeDead: {
    de: 'Diese Anmeldung ist abgelaufen oder es wurden zu viele Codes versucht. Bitte melden Sie sich erneut an.',
    en: 'This sign-in attempt expired, or too many codes were tried. Please start over from sign-in.',
  },
  mfaStartOver: { de: 'Erneut anmelden', en: 'Back to sign in' },
  mfaLostDevice: { de: 'Gerät verloren?', en: 'Lost your device?' },
  backToLogin: { de: 'Zurück zur Anmeldung', en: 'Back to sign in' },

  // 2FA recovery (self-service pages)
  recoverTitle: { de: 'Zwei-Faktor zurücksetzen', en: 'Reset two-factor' },
  recoverLead: {
    de: 'Geben Sie Ihre E-Mail ein. Wenn ein Konto existiert, senden wir einen einmaligen Link zum Zurücksetzen der Authenticator-App.',
    en: 'Enter your email. If an account exists, we will send a one-time link to reset your authenticator.',
  },
  recoverSend: { de: 'Reset-Link senden', en: 'Send reset link' },
  recoverSent: {
    de: 'Wenn das Konto existiert, ist ein Reset-Link unterwegs. Prüfen Sie Ihre E-Mails.',
    en: 'If the account exists, a reset link is on its way. Check your email.',
  },
  recoverConfirmTitle: { de: 'Authenticator zurücksetzen', en: 'Reset authenticator' },
  recoverConfirmLead: {
    de: 'Bestätigen Sie das Zurücksetzen. Beim nächsten Login richten Sie eine neue Authenticator-App ein.',
    en: 'Confirm the reset. At your next sign-in you will set up a new authenticator.',
  },
  recoverConfirmBtn: { de: 'Authenticator zurücksetzen', en: 'Reset authenticator' },
  recoverDone: {
    de: 'Authenticator entfernt. Melden Sie sich an, um einen neuen einzurichten.',
    en: 'Authenticator removed. Sign in to set up a new one.',
  },
  recoverBadToken: { de: 'Link ungültig oder bereits verwendet.', en: 'Link is invalid or already used.' },
} as const satisfies Namespace;
